/**
 * @tanstack/react-query hooks for G18 legal entities.
 *
 * Active entity preference is stored in sessionStorage under
 * "wr_active_entity_id" and injected into all API requests via the
 * Axios request interceptor (installEntityInterceptor). Install once
 * from a top-level component.
 *
 * Query-key convention:
 *   ["entities", "list"]
 *
 * Migration note (2026-06-06, Next.js → Vite Phase 2): port of
 * frontend/src/hooks/use-entities.ts. "use client" removed (all SPA
 * components are client components). next/navigation removed.
 */

import { useCallback, useEffect, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  archiveEntity,
  createEntity,
  listEntities,
  setEntityPrimary,
  switchEntity,
  updateEntity,
} from "@/lib/api/entities";
import type {
  CreateEntityRequest,
  LegalEntity,
  UpdateEntityRequest,
} from "@/lib/api/entities";
import { api } from "@/lib/api/client";
import { invalidatePayrollSetup } from "@/lib/payroll-setup-queries";

// ---------------------------------------------------------------------------
// Active-entity session storage key
// ---------------------------------------------------------------------------

const ACTIVE_ENTITY_KEY = "wr_active_entity_id";

export function getStoredEntityId(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_ENTITY_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  } catch {
    return null;
  }
}

export function setStoredEntityId(id: number | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id === null) {
      window.sessionStorage.removeItem(ACTIVE_ENTITY_KEY);
    } else {
      window.sessionStorage.setItem(ACTIVE_ENTITY_KEY, String(id));
    }
  } catch {
    // best effort
  }
}

// ---------------------------------------------------------------------------
// Axios interceptor — inject X-Entity-Id on every request when the user
// has a non-primary active entity chosen. Call installEntityInterceptor()
// once from a top-level module (entity-switcher.tsx does this on import).
// ---------------------------------------------------------------------------

let entityInterceptorId: number | null = null;
let entityHealInterceptorId: number | null = null;

export function installEntityInterceptor(): void {
  if (entityInterceptorId !== null) return;
  entityInterceptorId = api.interceptors.request.use((config) => {
    const id = getStoredEntityId();
    if (id !== null) {
      config.headers.set("X-Entity-Id", String(id));
    }
    return config;
  });
  // Self-heal (2026-07-05): a stale stored entity id — carried across a
  // tenant switch/registration, or pointing at a since-archived entity —
  // makes the fail-closed EntityScope middleware 403 EVERY request with
  // `entity_not_found`, before useActiveEntity's list-based fallback can
  // ever run (the entities list request itself carries the bad header).
  // On that exact error: drop the stored id and retry the request ONCE
  // without the header (the backend then scopes to the primary entity).
  // The auth boundaries also clear the id proactively (auth-context);
  // this catches every other staleness path.
  entityHealInterceptorId = api.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const cfg = error?.config;
      const data = error?.response?.data;
      if (
        error?.response?.status === 403 &&
        data?.error === "entity_not_found" &&
        cfg &&
        !cfg._entityRetried &&
        getStoredEntityId() !== null
      ) {
        setStoredEntityId(null);
        cfg._entityRetried = true;
        cfg.headers?.delete?.("X-Entity-Id");
        return api.request(cfg);
      }
      return Promise.reject(error);
    },
  );
}

export function removeEntityInterceptor(): void {
  if (entityInterceptorId !== null) {
    api.interceptors.request.eject(entityInterceptorId);
    entityInterceptorId = null;
  }
  if (entityHealInterceptorId !== null) {
    api.interceptors.response.eject(entityHealInterceptorId);
    entityHealInterceptorId = null;
  }
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const entityKeys = {
  all: ["entities"] as const,
  list: () => [...entityKeys.all, "list"] as const,
};

// ---------------------------------------------------------------------------
// useEntities — list all entities for the current tenant
// ---------------------------------------------------------------------------

export function useEntities() {
  return useQuery({
    queryKey: entityKeys.list(),
    queryFn: () => listEntities(),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// useActiveEntity — tracks the active entity in component state + storage
// ---------------------------------------------------------------------------

export function useActiveEntity(entities: LegalEntity[] | undefined) {
  const [activeId, setActiveIdState] = useState<number | null>(
    getStoredEntityId,
  );

  // When the entities list loads for the first time, resolve to primary if
  // nothing is stored yet.
  useEffect(() => {
    if (!entities?.length) return;
    const stored = getStoredEntityId();
    if (stored !== null) {
      // Verify the stored id still exists and is active.
      const match = entities.find((e) => e.id === stored && e.is_active);
      if (!match) {
        // Stale stored id — fall back to primary.
        const primary = entities.find((e) => e.is_primary);
        const fallback = primary?.id ?? entities[0]?.id ?? null;
        setStoredEntityId(fallback);
        setActiveIdState(fallback);
      }
      return;
    }
    // No stored id — default to primary.
    const primary = entities.find((e) => e.is_primary);
    const id = primary?.id ?? entities[0]?.id ?? null;
    setStoredEntityId(id);
    setActiveIdState(id);
  }, [entities]);

  const activeEntity =
    entities?.find((e) => e.id === activeId) ??
    entities?.find((e) => e.is_primary) ??
    entities?.[0] ??
    null;

  const setActiveId = useCallback((id: number) => {
    setStoredEntityId(id);
    setActiveIdState(id);
  }, []);

  return { activeEntity, activeId, setActiveId };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEntityRequest) => createEntity(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entityKeys.list() });
      // The checklist reads the active entity's TAN / PF / ESIC codes.
      invalidatePayrollSetup(qc);
    },
  });
}

export function useUpdateEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateEntityRequest }) =>
      updateEntity(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entityKeys.list() });
      // The checklist reads the active entity's TAN / PF / ESIC codes.
      invalidatePayrollSetup(qc);
    },
  });
}

export function useSetEntityPrimary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => setEntityPrimary(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entityKeys.list() });
      // The checklist reads the active entity's TAN / PF / ESIC codes.
      invalidatePayrollSetup(qc);
    },
  });
}

export function useArchiveEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => archiveEntity(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entityKeys.list() });
      // The checklist reads the active entity's TAN / PF / ESIC codes.
      invalidatePayrollSetup(qc);
    },
  });
}

export function useSwitchEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entityId: number) => switchEntity({ entity_id: entityId }),
    // The checklist is scoped to the active entity.
    onSuccess: () => invalidatePayrollSetup(qc),
  });
}
